import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import BarChartIcon from "@material-ui/icons/BarChart";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ContactOverviewModal from "../../components/ContactOverviewModal";
import NewTicketModal from "../../components/NewTicketModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import ContactAvatar from "../../components/ContactAvatar";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const getInitials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    mainPaper: {
      flex: 1,
      padding: 0,
      overflowY: "auto",
      borderRadius: 18,
      border: `1px solid ${
        isDark ? "rgba(45, 212, 191, 0.16)" : "rgba(148, 163, 184, 0.18)"
      }`,
      boxShadow: isDark
        ? "0 14px 34px rgba(2, 6, 23, 0.30)"
        : "0 10px 26px -16px rgba(15, 23, 42, 0.25)",
      background: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
      ...theme.scrollbarStyles,
    },
    headRow: {
      "& th": {
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        background: isDark ? "rgba(2, 12, 28, 0.7)" : "#f6f8fb",
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: "sticky",
        top: 0,
        zIndex: 2,
      },
    },
    row: {
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(45, 212, 191, 0.06)"
          : "rgba(20, 184, 166, 0.05)",
      },
    },
    avatar: {
      width: 38,
      height: 38,
      fontSize: "0.85rem",
      fontWeight: 700,
      color: "#ffffff",
      background: "linear-gradient(135deg, #36a08e 0%, #1f6f5c 100%)",
      border: "2px solid rgba(255, 255, 255, 0.9)",
      boxShadow: "0 6px 14px rgba(15, 23, 42, 0.12)",
    },
    nameCell: {
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    chipGroup: {
      fontWeight: 700,
      backgroundColor: isDark
        ? "rgba(20, 184, 166, 0.16)"
        : "rgba(18, 140, 126, 0.12)",
      color: isDark ? "#5eead4" : "#0f766e",
    },
    chipPrivate: {
      fontWeight: 700,
      backgroundColor: isDark
        ? "rgba(148, 163, 184, 0.18)"
        : "rgba(148, 163, 184, 0.16)",
      color: theme.palette.text.secondary,
    },
    chipPersonType: {
      fontWeight: 700,
      backgroundColor: isDark
        ? "rgba(59, 130, 246, 0.18)"
        : "rgba(59, 130, 246, 0.12)",
      color: isDark ? "#93c5fd" : "#2563eb",
    },
    chipBlocked: {
      marginLeft: 8,
      fontWeight: 700,
      backgroundColor: isDark
        ? "rgba(229, 72, 77, 0.20)"
        : "rgba(229, 72, 77, 0.12)",
      color: isDark ? "#fca5a5" : "#c4292e",
    },
    actionBtn: {
      color: theme.palette.text.secondary,
      "&:hover": { color: theme.palette.primary.main },
    },
    waBtn: {
      color: "#1f9d55",
      "&:hover": { color: "#15803d" },
    },
    searchField: {
      "& > div": {
        borderRadius: 999,
        backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
      },
    },
    advancedFilters: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1),
      borderRadius: 16,
    },
    filterGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
      gap: theme.spacing(2),
    },
    filterActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
  };
});

const Contacts = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [overviewContactId, setOverviewContactId] = useState(null);
  const [overviewModalOpen, setOverviewModalOpen] = useState(false);
  const [newTicketContact, setNewTicketContact] = useState(null);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const fileInputRef = React.useRef(null);

  const emptyFilters = {
    name: "",
    number: "",
    email: "",
    document: "",
    personType: "",
    isBlocked: "",
    dateFrom: "",
    dateTo: "",
    customFieldName: "",
    customFieldValue: "",
  };
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  // Os filtros so valem depois de "Aplicar": digitar num campo do painel não
  // deve disparar consulta a cada tecla como a barra simples faz.
  const [filters, setFilters] = useState(emptyFilters);
  const [customFieldNames, setCustomFieldNames] = useState([]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contacts/custom-field-names");
        setCustomFieldNames(Array.isArray(data) ? data : []);
      } catch (err) {
        setCustomFieldNames([]);
      }
    })();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, filters]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const params = { searchParam, pageNumber };

          Object.entries(filters).forEach(([key, value]) => {
            if (!value) return;
            // O input de data manda 00:00; sem empurrar para o fim do dia, o
            // filtro "até hoje" não traria os cadastros do proprio dia.
            params[key] = key === "dateTo" ? `${value}T23:59:59.999` : value;
          });

          const { data } = await api.get("/contacts/", { params });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, filters]);

  const handleDraftChange = (field) => (event) => {
    // O valor precisa ser lido agora, fora do updater: o React recicla o evento
    // sintetico assim que o handler retorna, e a função passada ao setState roda
    // depois - la event.target já e null e a tela quebra ao digitar.
    const { value } = event.target;
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => setFilters(draftFilters);

  const handleClearFilters = () => {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  };

  useEffect(() => {
    const socket = openSocket();

    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleOpenNewTicketModal = (contact) => {
    if (!contact?.id) return;
    setNewTicketContact(contact);
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = () => {
    setNewTicketModalOpen(false);
    setNewTicketContact(null);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportPhoneContacts = async () => {
    try {
      await api.post("/contacts/import");
      toast.success(i18n.t("contacts.toasts.imported"));
      dispatch({ type: "RESET" });
      setPageNumber(1);
    } catch (err) {
      toastError(err);
    }
  };

  const handleImportFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (!["csv", "txt"].includes(extension)) {
      toast.error(i18n.t("contacts.toasts.invalidImportFile"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setImportingFile(true);
    try {
      const { data } = await api.post("/contacts/import/file", formData);
      toast.success(
        i18n.t("contacts.toasts.fileImported", {
          imported: data?.imported || 0,
          skippedDuplicates: data?.skippedDuplicates || 0,
          skippedInvalid: data?.skippedInvalid || 0,
        })
      );
      dispatch({ type: "RESET" });
      setPageNumber(1);
    } catch (err) {
      toastError(err);
    } finally {
      setImportingFile(false);
    }
  };

  const handleOpenOverview = (contactId) => {
    setOverviewContactId(contactId);
    setOverviewModalOpen(true);
  };

  const handleCloseOverview = () => {
    setOverviewModalOpen(false);
    setOverviewContactId(null);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatDocument = value => {
    const digits = (value || "").replace(/\D/g, "");

    if (!digits) return "-";

    if (digits.length <= 11) {
      return digits
        .replace(/^(\d{3})(\d)/, "$1.$2")
        .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1-$2");
    }

    return digits
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ContactOverviewModal
        open={overviewModalOpen}
        contactId={overviewContactId}
        onClose={handleCloseOverview}
      />
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={newTicketContact}
        onClose={handleCloseNewTicketModal}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : ""
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => deletingContact && handleDeleteContact(deletingContact.id)}
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : ""}
      </ConfirmationModal>
      <MainHeader>
        <Title>{i18n.t("contacts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            variant="outlined"
            size="small"
            className={classes.searchField}
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant={activeFilterCount ? "contained" : "outlined"}
            color="primary"
            onClick={() => setAdvancedOpen((prev) => !prev)}
          >
            {activeFilterCount ? `Filtros (${activeFilterCount})` : "Filtros"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImportPhoneContacts}
            disabled={importingFile}
          >
            {i18n.t("contacts.buttons.importPhone")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImportFileClick}
            disabled={importingFile}
          >
            {i18n.t("contacts.buttons.importFile")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenContactModal}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {advancedOpen && (
        <Paper variant="outlined" className={classes.advancedFilters}>
          <div className={classes.filterGrid}>
            <TextField
              label="Nome"
              variant="outlined"
              size="small"
              value={draftFilters.name}
              onChange={handleDraftChange("name")}
            />
            <TextField
              label="Número"
              variant="outlined"
              size="small"
              value={draftFilters.number}
              onChange={handleDraftChange("number")}
              helperText="Pode digitar com mascara"
            />
            <TextField
              label="E-mail"
              variant="outlined"
              size="small"
              value={draftFilters.email}
              onChange={handleDraftChange("email")}
            />
            <TextField
              label="CPF/CNPJ"
              variant="outlined"
              size="small"
              value={draftFilters.document}
              onChange={handleDraftChange("document")}
            />
            <TextField
              select
              label="Tipo"
              variant="outlined"
              size="small"
              value={draftFilters.personType}
              onChange={handleDraftChange("personType")}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PF">Pessoa fisica</MenuItem>
              <MenuItem value="PJ">Pessoa juridica</MenuItem>
              <MenuItem value="NONE">Não informado</MenuItem>
            </TextField>
            <TextField
              select
              label="Situação"
              variant="outlined"
              size="small"
              value={draftFilters.isBlocked}
              onChange={handleDraftChange("isBlocked")}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="false">Liberados</MenuItem>
              <MenuItem value="true">Bloqueados</MenuItem>
            </TextField>
            <TextField
              type="date"
              label="Cadastrado de"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={draftFilters.dateFrom}
              onChange={handleDraftChange("dateFrom")}
            />
            <TextField
              type="date"
              label="Cadastrado até"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={draftFilters.dateTo}
              onChange={handleDraftChange("dateTo")}
            />
            <TextField
              select
              label="Campo adicional"
              variant="outlined"
              size="small"
              value={draftFilters.customFieldName}
              onChange={handleDraftChange("customFieldName")}
              helperText={
                customFieldNames.length
                  ? ""
                  : "Nenhum campo adicional cadastrado"
              }
            >
              <MenuItem value="">Qualquer</MenuItem>
              {customFieldNames.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Valor do campo"
              variant="outlined"
              size="small"
              value={draftFilters.customFieldValue}
              onChange={handleDraftChange("customFieldValue")}
            />
          </div>
          <div className={classes.filterActions}>
            <Button onClick={handleClearFilters}>Limpar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
            >
              Aplicar
            </Button>
          </div>
        </Paper>
      )}

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow className={classes.headRow}>
              <TableCell padding="checkbox" />
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.personType")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.document")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.email")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id} className={classes.row}>
                  <TableCell style={{ paddingRight: 0 }}>
                    <ContactAvatar contact={contact} className={classes.avatar}>
                      {getInitials(contact.name)}
                    </ContactAvatar>
                  </TableCell>
                  <TableCell className={classes.nameCell}>
                    {contact.name}
                    {contact.isBlocked ? (
                      <Chip
                        size="small"
                        label="Bloqueado"
                        className={classes.chipBlocked}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell align="center">
                    {contact.isGroup ? (
                      <Chip
                        size="small"
                        label="Grupo"
                        className={classes.chipGroup}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Privado"
                        className={classes.chipPrivate}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">{contact.number}</TableCell>
                  <TableCell align="center">
                    {contact.isGroup ? (
                      "-"
                    ) : contact.personType ? (
                      <Chip
                        size="small"
                        label={contact.personType}
                        className={classes.chipPersonType}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {contact.isGroup ? "-" : formatDocument(contact.document)}
                  </TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={i18n.t("contacts.table.viewHistory")}>
                      <IconButton
                        size="small"
                        className={classes.actionBtn}
                        onClick={() => handleOpenOverview(contact.id)}
                      >
                        <BarChartIcon />
                      </IconButton>
                    </Tooltip>
                    {!contact.isGroup && (
                      <IconButton
                        size="small"
                        className={classes.waBtn}
                        onClick={() => handleOpenNewTicketModal(contact)}
                      >
                        <WhatsAppIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      className={classes.actionBtn}
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          className={classes.actionBtn}
                          onClick={(e) => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={3} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
